// Scans HTML for tags/attributes that the Klue digest mailer's `digest_sanitize`
// helper strips before rendering the periodic-digest email (see
// klue/app/mailers/user_mailer.rb:571). The helper uses a strict allow-list of
// tags (b, i, em, strong, u, a, img, br, p, div, span, ol, ul, li, s, h1–h6)
// and attributes (href, src, style, width, alt, title); everything else is
// dropped — silently — before the email ships.
//
// Content added to a digest via `Add to Digest` (card menu or Ask Klue answer)
// therefore may render differently in the emailed digest than in the curator's
// preview UI. We surface a confirmation before the add so the curator can
// decide whether to proceed or edit their content first.

export const STRIP_CATEGORY = {
  iframe: 'iframe',
  table: 'table',
  video: 'video',
  audio: 'audio',
  blockquote: 'blockquote',
  figure: 'figure',
  dynamicBlock: 'dynamicBlock',
} as const;

export type StripCategory =
  (typeof STRIP_CATEGORY)[keyof typeof STRIP_CATEGORY];

// Ordered by curator-visible severity — the UI list follows this order.
export const STRIP_CATEGORIES_ORDER: readonly StripCategory[] = [
  STRIP_CATEGORY.iframe,
  STRIP_CATEGORY.dynamicBlock,
  STRIP_CATEGORY.table,
  STRIP_CATEGORY.video,
  STRIP_CATEGORY.audio,
  STRIP_CATEGORY.blockquote,
  STRIP_CATEGORY.figure,
];

// Selector per category. Any single match flags the category. Table sub-tags
// (tr/td/th/thead/tbody) are treated as "table" — no curator wants a separate
// warning per row-level element.
//
// `dynamicBlock` is selector-only for its new-format shape (see the raw-formula
// regex below for the legacy shape).
const CATEGORY_SELECTORS: Record<StripCategory, string> = {
  [STRIP_CATEGORY.iframe]: 'iframe',
  [STRIP_CATEGORY.table]: 'table, thead, tbody, tr, th, td',
  [STRIP_CATEGORY.video]: 'video, source',
  [STRIP_CATEGORY.audio]: 'audio',
  [STRIP_CATEGORY.blockquote]: 'blockquote',
  [STRIP_CATEGORY.figure]: 'figure, figcaption',
  [STRIP_CATEGORY.dynamicBlock]: '[data-block-type="dynamicBlock"]',
};

// Legacy dynamic-block formula tokens the Klue Compete 1.0 editor used before
// migrating to the `<div data-block-type="dynamicBlock">` placeholder. Some
// older cards still carry them in raw text. Mirrors klue's server-side
// `FORMULA_MATCHER` in `ingest/cards_controller.rb`.
// Bounded quantifiers (not `+`) so the eslint ReDoS check is satisfied — the
// character classes are disjoint from `{` / `}` and separated by a literal
// `COMPANY(`, but the linter flags any unbounded `+` next to another. 500
// chars per span is well above any real formula token.
const DYNAMIC_BLOCK_FORMULA_REGEX =
  /\{\{[^{}]{1,500}COMPANY\([^{}]{1,500}\}\}/m;

// Returns the categories present in `html`, in the order they should render in
// the warning list. Uses DOMParser so we get proper HTML parsing (entities,
// nested structure, self-closing tags) rather than regex heuristics.
export function detectStrippedElements(
  html: string | undefined | null,
): StripCategory[] {
  if (!html) return [];

  const doc = new DOMParser().parseFromString(html, 'text/html');
  const hasLegacyFormula = DYNAMIC_BLOCK_FORMULA_REGEX.test(html);

  return STRIP_CATEGORIES_ORDER.filter((category) => {
    if (
      category === STRIP_CATEGORY.dynamicBlock &&
      hasLegacyFormula &&
      !doc.body.querySelector(CATEGORY_SELECTORS[category])
    ) {
      return true;
    }
    return Boolean(doc.body.querySelector(CATEGORY_SELECTORS[category]));
  });
}
